import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  AnnouncementRecord,
  CreateAnnouncementRequest,
  CreateAnnouncementResponseData,
  EditAnnouncementRequest,
  EditAnnouncementResponseData,
} from "../../types";
import {
  CreateAnnouncement,
  EditAnnouncement,
  SearchAnnouncements,
} from "../../services/endpoints/announcement.endpoint";
import { logout } from "./authSlice";
import type { RootState } from "../store";
import { formatYMD } from "../../utils";

export interface AnnouncementState {
  records: AnnouncementRecord[];
  activeRecords: AnnouncementRecord[];
  isLoading: boolean;
  error: string | null;
}

const initialState: AnnouncementState = {
  records: [],
  activeRecords: [],
  isLoading: false,
  error: null,
};

/** Fetches announcements created by a teacher/master, for management on the Profile page. */
export const searchAnnouncementsThunk = createAsyncThunk<
  AnnouncementRecord[],
  { selectQuery: string; force?: boolean },
  { state: RootState; rejectValue: string }
>(
  "announcement/search",
  ({ selectQuery }, { rejectWithValue }) => {
    return SearchAnnouncements({ selectQuery })
      .then((res) => res.data.records || [])
      .catch((error: any) => rejectWithValue(error.message));
  },
  {
    condition: ({ force }, { getState }) => {
      if (force) return true;
      const { announcement } = getState();
      return announcement.records.length === 0 && !announcement.isLoading;
    },
  },
);

/** Fetches currently active announcements for students and teachers. */
export const getActiveAnnouncementsThunk = createAsyncThunk<
  AnnouncementRecord[],
  { force?: boolean } | void,
  { state: RootState; rejectValue: string }
>(
  "announcement/getActive",
  (_, { rejectWithValue }) => {
    const today = formatYMD(new Date());
    const selectQuery = `select id, Teacher.Name, Start_Date, End_Date, Description, Name, File_Link from Announcements where Name is not null and End_Date >= '${today}' order by Start_Date desc limit 0, 200`;

    return SearchAnnouncements({ selectQuery }, { skipLoader: true })
      .then((res) => res.data.records || [])
      .catch((error: any) => rejectWithValue(error.message));
  },
  {
    condition: (param, { getState }) => {
      if (param?.force) return true;
      const { announcement } = getState();
      return announcement.activeRecords.length === 0 && !announcement.isLoading;
    },
  },
);

export const createAnnouncementThunk = createAsyncThunk<
  CreateAnnouncementResponseData,
  CreateAnnouncementRequest,
  { state: RootState; rejectValue: string }
>("announcement/create", (payload, { rejectWithValue }) => {
  return CreateAnnouncement(payload)
    .then((res) => res.data)
    .catch((error: any) => rejectWithValue(error.message));
});

export const editAnnouncementThunk = createAsyncThunk<
  EditAnnouncementResponseData,
  EditAnnouncementRequest,
  { state: RootState; rejectValue: string }
>("announcement/edit", (payload, { rejectWithValue }) => {
  return EditAnnouncement(payload)
    .then((res) => res.data)
    .catch((error: any) => rejectWithValue(error.message));
});

const announcementSlice = createSlice({
  name: "announcement",
  initialState,
  reducers: {
    clearAnnouncementState: (state) => {
      state.records = [];
      state.activeRecords = [];
      state.isLoading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchAnnouncementsThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(searchAnnouncementsThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.records = action.payload;
        state.error = null;
      })
      .addCase(searchAnnouncementsThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || null;
      })
      .addCase(getActiveAnnouncementsThunk.fulfilled, (state, action) => {
        state.activeRecords = action.payload;
      })
      .addCase(getActiveAnnouncementsThunk.rejected, (state, action) => {
        state.error = action.payload || null;
      })
      .addCase(createAnnouncementThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createAnnouncementThunk.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(createAnnouncementThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || null;
      })
      .addCase(editAnnouncementThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(editAnnouncementThunk.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(editAnnouncementThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || null;
      })
      .addCase(logout, (state) => {
        state.records = [];
        state.activeRecords = [];
        state.isLoading = false;
        state.error = null;
      });
  },
});

export const { clearAnnouncementState } = announcementSlice.actions;
export default announcementSlice.reducer;
